import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiTrash2, FiSearch } from 'react-icons/fi';
import { database } from '../../Firebase';
import { ref, push, onValue, remove, update } from 'firebase/database';

const SupplierList = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { grampanchayatName, arthikVarshLabel } = location.state || {};

    const [viewMode, setViewMode] = useState('list'); // 'list' or 'form'
    const [searchTerm, setSearchTerm] = useState('');
    const [suppliers, setSuppliers] = useState([]);

    // Form State
    const initialFormState = {
        supplierNo: '',
        name: '',
        contactNo: '',
        address: '',
        gstReg: 'नोंदणी केलेली नाही', // Default: Not Registered
        accountNo: '',
        openingBalance: '',
        balanceType: 'जमा' // 'जमा' (Credit) or 'नावे' (Debit)
    };
    const [formData, setFormData] = useState(initialFormState);

    // Fetch Suppliers Data
    useEffect(() => {
        const suppliersRef = ref(database, 'Grampanchayat/Panipuravtha/Suppliers');
        onValue(suppliersRef, (snapshot) => {
            const data = snapshot.val();
            const loadedSuppliers = [];
            if (data) {
                Object.keys(data).forEach((key) => {
                    loadedSuppliers.push({
                        id: key,
                        ...data[key]
                    });
                });
            }
            // Sort by supplierNo if available
            loadedSuppliers.sort((a, b) => (parseInt(a.supplierNo) || 0) - (parseInt(b.supplierNo) || 0));
            setSuppliers(loadedSuppliers);
        });
    }, []);

    // Set next Supplier No automatically when adding new
    useEffect(() => {
        if (viewMode === 'form' && !formData.id) {
            const nextNo = suppliers.length > 0 
                ? Math.max(...suppliers.map(item => parseInt(item.supplierNo || 0))) + 1 
                : 1;
            setFormData(prev => ({ ...prev, supplierNo: nextNo }));
        }
    }, [viewMode, suppliers]);

    const handleInputChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    const handleEdit = (item) => {
        setFormData(item);
        setViewMode('form');
    };

    const handleSave = () => {
        if (!formData.name) {
            alert('कृपया पुरवठादाराचे नाव भरा');
            return;
        }

        const suppliersRef = ref(database, 'Grampanchayat/Panipuravtha/Suppliers');
        
        if (formData.id) {
            // Update existing
            const itemRef = ref(database, `Grampanchayat/Panipuravtha/Suppliers/${formData.id}`);
            update(itemRef, {
                ...formData,
                updatedAt: new Date().toISOString()
            })
            .then(() => {
                alert('माहिती यशस्वीरित्या अद्यतनित (Update) झाली');
                setFormData(initialFormState);
                setViewMode('list');
            })
            .catch((error) => {
                console.error("Error updating data: ", error);
                alert('माहिती अद्यतनित करताना त्रुटी आली');
            });
        } else {
            // Create new
            const newItem = {
                ...formData,
                createdAt: new Date().toISOString()
            };
            push(suppliersRef, newItem)
                .then(() => {
                    alert('माहिती यशस्वीरित्या साठवली');
                    setFormData(initialFormState);
                    setViewMode('list');
                })
                .catch((error) => {
                    console.error("Error saving data: ", error);
                    alert('माहिती साठवताना त्रुटी आली');
                });
        }
    };

    const handleDelete = (id) => {
        if(window.confirm('तुम्ही नक्की हि माहिती खोडू इच्छिता?')) {
            remove(ref(database, `Grampanchayat/Panipuravtha/Suppliers/${id}`));
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6 font-poppins">
            {/* Main Container */}
            <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-lg border border-blue-900 overflow-hidden">
                
                {/* Header - Blue Bar */}
                <div className="bg-blue-800 text-white p-4 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => viewMode === 'form' ? setViewMode('list') : navigate(-1)} 
                            className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors flex items-center justify-center mr-2"
                        >
                            <FiArrowLeft className="text-xl" />
                        </button>
                        <h1 className="text-xl font-bold">
                            {viewMode === 'list' ? 'पुरवठादार माहिती' : 'पुरवठादार फॉर्म'}
                        </h1>
                    </div>
                    {viewMode === 'list' && (
                        <button 
                            onClick={() => {
                                setFormData(initialFormState);
                                setViewMode('form');
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-1.5 rounded font-bold shadow-sm transition-colors text-sm"
                        >
                            नवीन
                        </button>
                    )}
                </div>

                {viewMode === 'list' ? (
                    // LIST VIEW
                    <>
                        {/* Search Section */}
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex gap-2">
                                <div className="relative flex-grow">
                                    <input 
                                        type="text" 
                                        placeholder="शोधणे" 
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full px-4 py-2 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-700"
                                    />
                                </div>
                                <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded font-bold shadow-sm transition-colors">
                                    शोधणे
                                </button>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-center border-collapse">
                                <thead className="bg-[#2c5282] text-white">
                                    <tr>
                                        <th className="px-4 py-3 border-r border-blue-400 font-medium">क्रमांक</th>
                                        <th className="px-4 py-3 border-r border-blue-400 font-medium text-left w-1/4">पुरवठादाराचे नाव</th>
                                        <th className="px-4 py-3 border-r border-blue-400 font-medium">संपर्क नं.</th>
                                        <th className="px-4 py-3 border-r border-blue-400 font-medium">खातावणी क्र.</th>
                                        <th className="px-4 py-3 border-r border-blue-400 font-medium">खाते क्र.</th>
                                        <th className="px-4 py-3 border-r border-blue-400 font-medium">GST नोंदणी</th>
                                        <th className="px-4 py-3 font-medium">पर्याय</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-300">
                                    {suppliers.filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase())).map((item) => (
                                        <tr key={item.id} className="hover:bg-blue-50 transition-colors bg-white">
                                            <td className="px-4 py-3 border-r border-gray-300 text-gray-800">{item.supplierNo}</td>
                                            <td className="px-4 py-3 border-r border-gray-300 text-left text-gray-800">{item.name}</td>
                                            <td className="px-4 py-3 border-r border-gray-300 text-gray-800">{item.contactNo}</td>
                                            <td className="px-4 py-3 border-r border-gray-300 text-gray-800">595</td>
                                            <td className="px-4 py-3 border-r border-gray-300 text-gray-800">{item.accountNo}</td>
                                            <td className="px-4 py-3 border-r border-gray-300 text-gray-800">
                                                {item.gstReg !== 'नोंदणी केलेली नाही' ? 'Yes' : ''}
                                            </td>
                                            <td className="px-4 py-3 flex justify-center gap-2">
                                                <button 
                                                    onClick={() => handleEdit(item)}
                                                    className="bg-[#1e40af] text-white px-4 py-1.5 rounded hover:bg-blue-800 transition-colors text-sm"
                                                >
                                                    बदल
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(item.id)}
                                                    className="bg-[#ef4444] text-white px-4 py-1.5 rounded hover:bg-red-600 transition-colors text-sm"
                                                >
                                                    खोडणे
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {suppliers.length === 0 && (
                                        <tr>
                                            <td colSpan="7" className="text-center py-8 text-gray-500">माहिती उपलब्ध नाही</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Footer */}
                        <div className="bg-gradient-to-b from-blue-700 to-blue-900 p-2 flex justify-center gap-1">
                            <button className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-500 border border-blue-400">|&lt;</button>
                            <button className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-500 border border-blue-400">&lt;&lt;</button>
                            <button className="px-3 py-1 bg-teal-600 text-white rounded text-xs hover:bg-teal-500 border border-teal-400 font-bold">1</button>
                            <button className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-500 border border-blue-400">&gt;&gt;</button>
                            <button className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-500 border border-blue-400">&gt;|</button>
                            <div className="ml-2 bg-white rounded px-2 flex items-center">
                                <select className="text-xs font-bold text-gray-700 outline-none">
                                    <option>5</option>
                                    <option>10</option>
                                    <option>20</option>
                                </select>
                            </div>
                        </div>
                    </>
                ) : (
                    // FORM VIEW
                    <div className="p-6 md:p-8">
                    <div className="grid grid-cols-1 gap-6">
                        {/* Supplier No (Read Only) */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                            <label className="text-gray-700 font-medium md:text-right">पुरवठादार क्र.:</label>
                            <input 
                                type="text" 
                                value={formData.supplierNo}
                                readOnly
                                className="md:col-span-2 w-full p-2 border border-blue-300 rounded bg-gray-200 cursor-not-allowed"
                            />
                        </div>

                        {/* Name */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                            <label className="text-gray-700 font-medium md:text-right">पुरवठादाराचे नाव:</label>
                            <input 
                                type="text" 
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder="पुरवठादाराचे नाव भरा"
                                className="md:col-span-3 w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Contact No */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                            <label className="text-gray-700 font-medium md:text-right">संपर्क क्र.:</label>
                            <input 
                                type="text" 
                                value={formData.contactNo}
                                onChange={(e) => handleInputChange('contactNo', e.target.value)}
                                placeholder="संपर्क क्र. भरा"
                                className="md:col-span-2 w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Address */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                            <label className="text-gray-700 font-medium md:text-right">पत्ता:</label>
                            <textarea
                                value={formData.address}
                                onChange={(e) => handleInputChange('address', e.target.value)}
                                placeholder="पत्ता भरा"
                                rows="2"
                                className="md:col-span-3 w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* GST Registration */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                            <label className="text-gray-700 font-medium md:text-right">GST नोंदणी:</label>
                            <select 
                                value={formData.gstReg}
                                onChange={(e) => handleInputChange('gstReg', e.target.value)}
                                className="md:col-span-2 w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="नोंदणी केलेली नाही">नोंदणी केलेली नाही</option>
                                <option value="नोंदणी केलेली आहे">नोंदणी केलेली आहे</option>
                            </select>
                        </div>

                        {/* Account No */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                            <label className="text-gray-700 font-medium md:text-right">खाते क्र.:</label>
                            <input 
                                type="text" 
                                value={formData.accountNo}
                                onChange={(e) => handleInputChange('accountNo', e.target.value)}
                                placeholder="खाते क्र."
                                className="md:col-span-2 w-full p-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
                            />
                        </div>

                        {/* Opening Balance */}
                        <div className="grid grid-cols-1 md:grid-cols-4 items-center gap-4">
                            <label className="text-gray-700 font-medium md:text-right">आरंभीची शिल्लक:</label>
                            <div className="md:col-span-3 flex items-center gap-4">
                                <input 
                                    type="number" 
                                    value={formData.openingBalance}
                                    onChange={(e) => handleInputChange('openingBalance', e.target.value)}
                                    placeholder="आरंभीची शिल्लक रक्कम भरा"
                                    className="w-full md:w-1/2 p-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input 
                                            type="radio" 
                                            name="balanceType" 
                                            value="जमा"
                                            checked={formData.balanceType === 'जमा'}
                                            onChange={(e) => handleInputChange('balanceType', e.target.value)}
                                            className="w-4 h-4 text-blue-600"
                                        />
                                        <span>जमा</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input 
                                            type="radio" 
                                            name="balanceType" 
                                            value="नावे"
                                            checked={formData.balanceType === 'नावे'}
                                            onChange={(e) => handleInputChange('balanceType', e.target.value)}
                                            className="w-4 h-4 text-blue-600"
                                        />
                                        <span>नावे</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-center gap-4 mt-8 bg-blue-900 p-4 -mx-6 -mb-6 md:-mx-8 md:-mb-8 rounded-b-xl">
                            <button 
                                onClick={handleSave}
                                className="bg-green-600 hover:bg-green-700 text-white px-8 py-2 rounded-lg font-bold shadow transition-colors"
                            >
                                साठविणे
                            </button>
                            <button 
                                onClick={() => setViewMode('list')}
                                className="bg-red-500 hover:bg-red-600 text-white px-8 py-2 rounded-lg font-bold shadow transition-colors"
                            >
                                रद्द करणे
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    </div>
    );
};

export default SupplierList;
